
import {React,useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Paper,
  IconButton,
  InputAdornment,
  Divider,
} from "@mui/material";

// ✅ MUI Icons
import {
  Visibility,
  VisibilityOff,
  EmailOutlined,
  LockOutlined,
  Facebook,
  Apple,
} from "@mui/icons-material";
import GoogleIcon from "@mui/icons-material/Google";

import { useForm, Controller } from "react-hook-form";
import { useDispatch } from "react-redux";
import AuthViewController from "../../redux/actions/AuthViewController";
import ResumeImg from "../../assets/women-8080878_1280.png";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/Thryiv_logo_tm_TB.png";

const OtpResetPassword = () => {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm();
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();
  const { login } = AuthViewController();
  const navigate = useNavigate();

  const [otpVerified, setOtpVerified] = useState(false);

  const otp = watch("otp");

  const onSubmit = (data) => {
    if (!otpVerified) {
      if (otp && otp.length === 6) {
        setOtpVerified(true);
      } else {
        alert("Please enter a valid 6-digit OTP.");
      }
    } else {
      if (data.newPassword !== data.confirmPassword) {
        alert("Passwords do not match.");
        return;
      }
      dispatch(login(data)); // handle actual reset
    }
  };

  return (
    <Grid container sx={{ height: "100vh", width: "100vw" }}>
      {/* Left Panel (remains unchanged) */}
      <Grid
      item
      xs={12}
      md={6}
      sx={{
        position: "relative", // To contain the overlay
        color: "#fff",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        textAlign: "center",
        p: 4,
        minHeight: "50vh",
        backgroundImage: `url(${ResumeImg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Overlay */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          bgcolor: "rgba(0, 0, 0, 0.6)", // Black overlay with 60% opacity
          zIndex: 1,
        }}
      />
    
      {/* Content */}
      <Box sx={{ position: "relative", zIndex: 2 }}>
        <Typography
          variant="h4"
          fontWeight="bold"
          sx={{
            fontSize: { xs: "1.8rem", md: "2.4rem" },
          }}
        >
          Empowering people through seamless HR management.
        </Typography>
    
        <Typography
          variant="body1"
          sx={{
            mt: 2,
            maxWidth: "80%",
            fontSize: { xs: "0.9rem", md: "1rem" },
          }}
        >
          Efficiently manage your workforce, streamline operations effortlessly.
        </Typography>
      </Box>
    </Grid>

      {/* Right Panel */}
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          backgroundColor: "#fff",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: { xs: 2, md: 4 },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 400 }}>
          <Paper sx={{ p: 4, width: "100%", boxShadow: 3, borderRadius: 2 }}>
             <Box display="flex" justifyContent="center" alignItems="center">
                          <img
                            src={logo}
                            alt="Company Logo"
                            style={{
                              height: 60, // adjust height as needed
                              objectFit: "contain", // keeps aspect ratio
                            }}
                          />
                        </Box>
            <Typography variant="h6" align="center" sx={{ mt: 2, fontWeight: "bold", mb: 2 }}>
              Password Reset
            </Typography>
            <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
              We’ve sent a code to your email address
            </Typography>

            <form onSubmit={handleSubmit(onSubmit)}>
              {/* OTP Field */}
              <Controller
                name="otp"
                control={control}
                rules={{
                  required: "OTP is required",
                  minLength: { value: 6, message: "OTP must be 6 digits" },
                  maxLength: { value: 6, message: "OTP must be 6 digits" },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Enter 6-digit OTP"
                    fullWidth
                    variant="outlined"
                    sx={{ mb: 2 }}
                    error={!!errors.otp}
                    helperText={errors.otp?.message}
                    inputProps={{ maxLength: 6 }}
                  />
                )}
              />

              {/* Password Fields - show only after OTP verification */}
              {otpVerified && (
                <>
                  <Controller
                    name="newPassword"
                    control={control}
                    rules={{
                      required: "New Password is required",
                      minLength: { value: 6, message: "Min 6 characters required" },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="New Password"
                        type={showPassword ? "text" : "password"}
                        fullWidth
                        variant="outlined"
                        sx={{ mb: 2 }}
                        error={!!errors.newPassword}
                        helperText={errors.newPassword?.message}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowPassword(!showPassword)}>
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                  <Controller
                    name="confirmPassword"
                    control={control}
                    rules={{
                      required: "Please confirm password",
                      validate: (value) =>
                        value === watch("newPassword") || "Passwords do not match",
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Confirm Password"
                        type="password"
                        fullWidth
                        variant="outlined"
                        sx={{ mb: 2 }}
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword?.message}
                      />
                    )}
                  />
                </>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  backgroundColor: "#FF6A00",
                  "&:hover": { backgroundColor: "#E65A00" },
                  mb: 2,
                  py: 1.4,
                }}
              >
                {otpVerified ? "Reset Password" : "Verify OTP"}
              </Button>

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1, mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ color: "error.main", cursor: "pointer" }}
                  onClick={() => navigate("/login")}
                >
                  Back to Login ?
                </Typography>
              </Box>

              <Typography variant="caption" display="block" align="center" sx={{ mt: 4, color: "text.secondary" }}>
                Copyright © 2024 - SmartHR
              </Typography>
            </form>
          </Paper>
        </Box>
      </Grid>
    </Grid>
  );
};

export default OtpResetPassword;

